
import { format, formatDistanceToNow, differenceInHours, isValid, parseISO } from 'date-fns';

/**
 * Formats an ISO date string into a user-friendly estimated completion time.
 * Example: "Est. completion in 5 minutes" or "Est. completion by 5:30 PM on May 28"
 * Returns null if the date is invalid, not provided, or in the past.
 */
export const formatEstimatedCompletionTime = (isoString: string | null | undefined): string | null => {
  if (!isoString) {
    return null;
  }

  const date = parseISO(isoString);
  if (!isValid(date)) {
    return null;
  }

  const now = new Date();
  // Consider "past" if it's more than a minute ago to avoid showing "in a few seconds" for already passed times.
  if (date < new Date(now.getTime() - 60 * 1000)) { 
    return null; // Or "Est. completion: overdue"; for now, null.
  }

  const diffHours = differenceInHours(date, now);

  let formattedTime: string;
  if (date > now && diffHours < 12) {
    // For times in the near future (less than 12 hours away)
    formattedTime = formatDistanceToNow(date, { addSuffix: true });
  } else {
    // For times further in the future or on different days
    formattedTime = format(date, "h:mm a 'on' MMM d");
  }

  return `Est. completion ${formattedTime}`;
};

/**
 * Formats a number as currency.
 * Placeholder implementation.
 */
export const formatCurrency = (value: number | null | undefined, currency: string = 'USD'): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
};

/**
 * Formats a value for display in a tooltip.
 * Placeholder implementation.
 */
export const formatTooltipValue = (value: any, label?: string): string => {
  if (value === null || value === undefined) {
    return '';
  }
  let formattedValue = String(value);
  if (typeof value === 'number') {
    formattedValue = value.toLocaleString();
  }
  return label ? `${label}: ${formattedValue}` : formattedValue;
};

/**
 * Sanitizes a URL.
 * Placeholder implementation - ensure this matches any previous real implementation if needed.
 */
export const sanitizeUrl = (url: string | null | undefined): string => {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

